import React from 'react';
import { useTranslation } from 'react-i18next';
import { subProjectService } from '../services/subProjectService';
import RichTextEditor from './RichTextEditor';

interface DirectFieldPaneProps {
  subProjectId: string;
  field: string;
  fieldLabel: string;
  solutionId?: string | null;
}

const DirectFieldPane: React.FC<DirectFieldPaneProps> = ({
  subProjectId, field, fieldLabel, solutionId: _solutionId,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const sp = await subProjectService.getById(subProjectId);
        const raw = (sp as unknown as Record<string, unknown>)[field];
        if (field === 'state_of_the_art') {
          const sota = raw as { content?: string } | null | undefined;
          setValue(sota?.content !== undefined ? (sota.content || '') : String(raw ?? ''));
        } else {
          setValue(String(raw ?? ''));
        }
      } catch { /* ignore */ }
      setLoaded(true);
    })();
  }, [subProjectId, field]);

  const handleChange = (html: string) => {
    setValue(html);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await subProjectService.update(subProjectId, { [field]: html });
      } catch { /* ignore */ }
      setSaving(false);
    }, 2000);
  };

  if (!loaded) return <div className="p-3 text-xs text-slate-400">{t('common.loading', 'Loading...')}</div>;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <span className="text-xs font-semibold text-slate-600 flex-1">{fieldLabel}</span>
        {saving && <span className="text-[10px] text-slate-400">{t('common.saving', 'Saving...')}</span>}
      </div>
      <RichTextEditor
        content={value}
        onChange={handleChange}
        editable={true}
        enableImages={false}
        placeholder={t('workspace.directEdit.placeholder', 'Type here...')}
      />
    </div>
  );
};

export default DirectFieldPane;
